import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Leaf, Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const AdminLogin = () => {
  const { language, toggleLanguage } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const { status, login } = useAdminAuth();
  const location = useLocation();
  const from = location.state?.from || '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (status === 'authed') return <Navigate to={from} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setError(res.error || t.invalidCreds);
  };

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className="min-h-screen flex items-center justify-center px-4
                 bg-gradient-to-br from-slate-50 via-cyan-50/50 to-emerald-50/40
                 dark:from-slate-950 dark:via-[#0a1929] dark:to-[#0a2418]"
    >
      {/* Ambient EBC brand glows */}
      <div className="fixed top-[8%] -left-20 w-[500px] h-[500px] rounded-full blur-[120px] bg-brand-blue/10 -z-10 pointer-events-none" />
      <div className="fixed bottom-[8%] -right-20 w-[500px] h-[500px] rounded-full blur-[120px] bg-brand-green/10 -z-10 pointer-events-none" />

      <div className="w-full max-w-md">
        {/* Language toggle */}
        <button
          type="button"
          onClick={toggleLanguage}
          data-testid="admin-lang-toggle"
          className="absolute top-6 right-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                     bg-white/80 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700
                     text-xs font-semibold text-slate-700 dark:text-slate-200
                     backdrop-blur hover:shadow transition"
        >
          {t.languageSwitch}
        </button>

        {/* Card */}
        <div className="rounded-3xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-800
                        shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)] backdrop-blur-xl p-8 md:p-10">
          {/* Brand */}
          <div className={`flex items-center gap-3 mb-7 ${isAr ? 'flex-row-reverse' : ''}`}>
            <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-green flex items-center justify-center shadow-md">
              <Leaf className="w-6 h-6 text-white" />
            </span>
            <div className={isAr ? 'text-right' : 'text-left'}>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">{t.brand}</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">{t.loginSubtitle}</p>
            </div>
          </div>

          <h2 className={`text-2xl font-bold text-slate-900 dark:text-white mb-6 ${isAr ? 'text-right' : 'text-left'}`}>
            {t.loginTitle}
          </h2>

          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-5">
            {/* Email */}
            <div>
              <label className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${isAr ? 'text-right' : 'text-left'}`}>
                {t.email}
              </label>
              <div className={`relative flex items-center ${isAr ? 'flex-row-reverse' : ''}`}>
                <Mail className={`absolute ${isAr ? 'right-3' : 'left-3'} w-4 h-4 text-slate-400`} />
                <input
                  type="email"
                  required
                  autoComplete="off"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isAr ? 'أدخل البريد الإلكتروني' : 'Enter email address'}
                  className={`w-full ${isAr ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'} py-3 rounded-xl
                              bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                              text-slate-900 dark:text-white placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/50
                              transition`}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${isAr ? 'text-right' : 'text-left'}`}>
                {t.password}
              </label>
              <div className={`relative flex items-center ${isAr ? 'flex-row-reverse' : ''}`}>
                <Lock className={`absolute ${isAr ? 'right-3' : 'left-3'} w-4 h-4 text-slate-400`} />
                <input
                  type="password"
                  required
                  autoComplete="off"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full ${isAr ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'} py-3 rounded-xl
                              bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                              text-slate-900 dark:text-white placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue/50
                              transition`}
                />
              </div>
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className={`flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-400/30
                            text-sm text-red-700 dark:text-red-300 ${isAr ? 'flex-row-reverse text-right' : 'text-left'}`}
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl
                         bg-gradient-to-r from-brand-blue to-brand-blue-700
                         hover:from-brand-blue-700 hover:to-brand-blue-800
                         text-white text-sm font-semibold shadow-md hover:shadow-lg
                         transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? t.signingIn : t.signIn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
